import { useState, useCallback, useEffect, useMemo, type ReactNode, useRef } from 'react';
import { logger } from '@/lib/logger';
import { SidebarContext } from './useSidebarContext';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved !== null ? JSON.parse(saved) : false; // default: expanded
    } catch (error) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('Failed to parse sidebar state from localStorage', error);
      }
      return false; // default: expanded
    }
  });

  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(isCollapsed));
    } catch (error) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('Failed to save sidebar state', error);
      }
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => setIsNavigating(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isNavigating]);

  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  
  const handleMouseEnter = useCallback((itemId: string) => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set hover state immediately for responsive feel
    setHoveredItemId(itemId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Debounce hover leave to prevent flickering during quick movements
    debounceTimerRef.current = setTimeout(() => {
      setHoveredItemId(null);
    }, 50);
  }, []);

  const setNavigating = useCallback((navigating: boolean) => {
    setIsNavigating(navigating);
  }, []);

  const contextValue = useMemo(() => ({
    isCollapsed,
    hoveredItemId,
    isNavigating,
    toggleSidebar,
    handleMouseEnter,
    handleMouseLeave,
    setNavigating
  }), [isCollapsed, hoveredItemId, isNavigating, toggleSidebar, handleMouseEnter, handleMouseLeave, setNavigating]);

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
}

